import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Edit, ArrowLeft, User, Phone, Mail, MapPin, UserCheck, CreditCard, Search, X, AlertCircle, Plus, Minus, Check, Save, Calendar, Activity, FileText, Eye , RotateCcw} from 'lucide-react';
import patientService from '../../patient/services/patientService';
import HistoryContent from '../components/PatientFileContents/HistoryContent';

const ClinicianPatientHistoryPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  
  // State for patient details
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  // State for search inputs (editable)
  const [searchData, setSearchData] = useState({
    patientId: '',
    patientName: '',
    patientDOB: ''
  });

  // State for patient search and selection
  const [showPatientIdDropdown, setShowPatientIdDropdown] = useState(false);
  const [showPatientNameDropdown, setShowPatientNameDropdown] = useState(false);
  const [filteredPatientsById, setFilteredPatientsById] = useState([]);
  const [filteredPatientsByName, setFilteredPatientsByName] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isPatientSelected, setIsPatientSelected] = useState(false);

  // State for real patient data from API
  const [allPatients, setAllPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // State for toast notifications
  const [toast, setToast] = useState(null);

  // State for collapsible search section (closed by default)
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // Set page title and fetch patient data
  useEffect(() => {
    document.title = 'MediFusion - Patient History';
    fetchPatientData();
    fetchAllPatients();
  }, [patientId]);

  // Update search fields when patient data is loaded
  useEffect(() => {
    if (patient) {
      setSearchData({
        patientId: patient.patient_id || '',
        patientName: `${patient.first_name} ${patient.last_name}`,
        patientDOB: patient.dob || ''
      });
      setSelectedPatient(patient);
      setIsPatientSelected(true);
    }
  }, [patient]);

  // Fetch patient data from API
  const fetchPatientData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching patient details for ID:', patientId);
      
      const patientData = await patientService.getPatientByPatientId(patientId);
      console.log('👤 Patient data from API:', patientData);
      
      setPatient(patientData);
    } catch (error) {
      console.error('❌ Error fetching patient data:', error);
      setPatient(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all patients from the database
  const fetchAllPatients = async () => {
    try {
      setLoadingPatients(true);
      console.log('🔍 Fetching all patients from database...');
      const response = await patientService.getAllPatients();
      console.log('👥 Patients from API:', response);
      
      const patients = response.patients || response || [];
      setAllPatients(patients);
      console.log('✅ Loaded', patients.length, 'patients from database');
    } catch (error) {
      console.error('❌ Error fetching patients:', error);
      setAllPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Filter patients by ID
  const filterPatientsById = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredPatientsById([]);
      return;
    }

    const filtered = allPatients.filter(patient => 
      patient.patient_id && patient.patient_id.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPatientsById(filtered);
  };

  // Filter patients by name
  const filterPatientsByName = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredPatientsByName([]);
      return;
    }

    const filtered = allPatients.filter(patient => 
      `${patient.first_name} ${patient.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPatientsByName(filtered);
  };

  // Handle patient ID input change
  const handlePatientIdChange = (value) => {
    setSearchData(prev => ({ ...prev, patientId: value }));
    filterPatientsById(value);
    if (value) {
      setShowPatientIdDropdown(true);
      setShowPatientNameDropdown(false);
    }
  };

  // Handle patient name input change
  const handlePatientNameChange = (value) => {
    setSearchData(prev => ({ ...prev, patientName: value }));
    filterPatientsByName(value);
    if (value) {
      setShowPatientNameDropdown(true);
      setShowPatientIdDropdown(false);
    }
  };

  // Handle patient selection from dropdown
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    
    // Format DOB for the date input field
    let formattedDOB = '';
    if (patient.dob && patient.dob !== '1900-01-01' && patient.dob !== null) {
      formattedDOB = patient.dob; // API already returns YYYY-MM-DD format
      console.log(`✅ DOB populated for ${patient.first_name} ${patient.last_name}: ${patient.dob}`);
    } else {
      console.log(`⚠️ No valid DOB found for ${patient.first_name} ${patient.last_name}`);
    }
    
    setSearchData({
      patientId: patient.patient_id,
      patientName: `${patient.first_name} ${patient.last_name}`,
      patientDOB: formattedDOB
    });
    setIsPatientSelected(true);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
  };

  // Show toast notification
  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    
    if (!selectedPatient) {
      showToast('Please select a patient first', 'error');
      return;
    }

    if (!searchData.patientDOB) {
      showToast('Please enter the patient\'s date of birth', 'error');
      return;
    }

    // Check if DOB matches
    if (searchData.patientDOB === selectedPatient.dob) {
      // Navigate to patient history page
      navigate(`/clinician/patientfiles/${selectedPatient.patient_id}/history`);
    } else {
      showToast('DOB does not match for the selected patient. Please verify and try again.', 'error');
    }
  };

  // Handle new patient button
  const handleNewPatient = () => {
    setSearchData({
      patientId: '',
      patientName: '',
      patientDOB: ''
    });
    setSelectedPatient(null);
    setIsPatientSelected(false);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">Loading patient history...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Patient Not Found</h2>
          <p className="text-gray-600 mb-4">The patient with ID {patientId} could not be found.</p>
          <Link
            to="/clinician/patientfiles"
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Patient Files
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Top Banner */}
      <div className="bg-blue-600 text-white px-6 py-1.5 rounded-lg mb-6">
        <h1 className="text-lg font-semibold text-center">Patient Files</h1>
      </div>

      {/* White Content Container with Search Block */}
      <div className={`bg-white rounded-lg shadow-md mb-4 ${isSearchExpanded ? "p-4" : "p-3"}`}>
        {/* Search Section */}
        <div className={isSearchExpanded ? "mb-4" : "mb-2"}>
          {/* Search Header with Icon and Toggle */}
          <div className={`flex items-center justify-between ${isSearchExpanded ? "mb-2" : "mb-0"}`}>
            <div className="flex items-center space-x-2">
              <Search className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-800">Patient Search</h2>
            </div>
            <button
              type="button"
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className={`bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center justify-center transition-colors shadow-sm ${isSearchExpanded ? "w-8 h-8" : "w-7 h-7"}`}
            >
              {isSearchExpanded ? (
                <Minus className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </button>
          </div>
          
          {/* Collapsible Content */}
          {isSearchExpanded && (
            <div>
              {/* Blue stylish divider */}
              <div className="h-0.5 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mb-4"></div>
              
              {/* Search Form */}
              <form onSubmit={handleSearch} className="space-y-2">
                {/* Input Fields and Buttons Row - All in One Line */}
                <div className="flex items-end space-x-3">
                  {/* Patient ID Input */}
                  <div className="flex-1 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID:</label>
                    <input
                      type="text"
                      placeholder="Enter Patient ID"
                      value={searchData.patientId}
                      onChange={(e) => handlePatientIdChange(e.target.value)}
                      onFocus={() => setShowPatientIdDropdown(true)}
                      disabled={isPatientSelected}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                    />
                    
                    {/* Dropdown for patient selection by ID */}
                    {showPatientIdDropdown && filteredPatientsById.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                        {filteredPatientsById.map((patient) => (
                          <div
                            key={patient.id || patient.patient_id}
                            onClick={() => handlePatientSelect(patient)}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900 text-sm">{patient.patient_id}</div>
                            <div className="text-xs text-gray-600">{patient.first_name} {patient.last_name}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Patient Name Input */}
                  <div className="flex-1 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                    <input
                      type="text"
                      placeholder="Enter Patient Name"
                      value={searchData.patientName}
                      onChange={(e) => handlePatientNameChange(e.target.value)}
                      onFocus={() => setShowPatientNameDropdown(true)}
                      disabled={isPatientSelected}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                    />
                    
                    {/* Dropdown for patient selection by name */}
                    {showPatientNameDropdown && filteredPatientsByName.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                        {filteredPatientsByName.map((patient) => (
                          <div
                            key={patient.id || patient.patient_id}
                            onClick={() => handlePatientSelect(patient)}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900 text-sm">{patient.first_name} {patient.last_name}</div>
                            <div className="text-xs text-gray-600">{patient.patient_id}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Patient DOB Input */}
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth:</label>
                    <input
                      type="date"
                      value={searchData.patientDOB}
                      onChange={(e) => setSearchData(prev => ({ ...prev, patientDOB: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex space-x-2">
                    <button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-md transition-colors text-sm font-medium"
                    >
                      Search
                    </button>
                    <button
                      type="button"
                      onClick={handleNewPatient}
                      className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-1.5 rounded-md transition-colors text-sm font-medium"
                    >
                      Reset Fields
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Patient Name Header */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg mb-6 shadow-sm relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-200 rounded-full -translate-y-12 translate-x-12"></div>
          <div className="absolute bottom-0 right-0 w-16 h-16 bg-blue-300 rounded-full translate-y-8 translate-x-8"></div>
        </div>
        
        {/* Patient name with enhanced styling */}
        <div className="relative z-10">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-5 bg-blue-500 rounded-full"></div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide leading-tight">
                {patient.first_name} {patient.last_name}
              </h2>
              <div className="h-0.5 w-12 bg-blue-500 mt-1 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Left Navigation Sidebar and Main Content */}
      <div className="flex gap-6">
        <div className="w-64">
          <div className="bg-white rounded-lg shadow-md p-4">
            <nav className="space-y-2">
              <Link 
                to={`/clinician/patientfiles/${patientId}/bookings`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Bookings
              </Link>
              <Link 
                to={`/clinician/patientfiles/${patientId}/general_details`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                General Details
              </Link>
              <Link 
                to={`/clinician/patientfiles/${patientId}/medical_details`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Medical Details
              </Link>
              <button className="w-full text-left px-3 py-2 rounded-md bg-blue-600 text-white transition-colors">
                History
              </button>
              <Link 
                to={`/clinician/patientfiles/${patientId}/analytics`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Analytics
              </Link>
              <Link 
                to={`/clinician/patientfiles/${patientId}/documents`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Documents
              </Link>
            </nav>
          </div>
        </div>

        {/* Main Content Area - History */}
        <div className="flex-1">
          <HistoryContent patientId={patientId} />
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 transition-all duration-300 transform ${
          toast.type === 'error' 
            ? 'bg-red-500 text-white' 
            : 'bg-green-500 text-white'
        }`}>
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="font-medium">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:bg-black hover:bg-opacity-20 rounded-full p-1 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ClinicianPatientHistoryPage;